import { useEffect, useState } from "react";
import {
  createFileRoute,
  Link,
  Outlet,
  useRouter,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { getAgencyOverview } from "@/lib/agency.functions";
import { buildHead } from "@/lib/seo";

const GOLD = "#C8A84B";

export const Route = createFileRoute("/agency")({
  head: () =>
    buildHead({
      locale: "en",
      title: "Agency dashboard · ShugertGEO",
      description: "Launch white-label GEO audits and manage your credits.",
      path: "/agency",
      altPath: "/agency",
      noindex: true,
    }),
  component: AgencyLayout,
});

function AgencyLayout() {
  const router = useRouter();
  const [email, setEmail] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const fetchOverview = useServerFn(getAgencyOverview);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setEmail(data.session?.user.email ?? null);
      setHydrated(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setEmail(session?.user.email ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (hydrated && !email) {
      router.navigate({
        to: "/agency-login",
        search: { redirect: "/agency" },
      });
    }
  }, [hydrated, email, router]);

  const overview = useQuery({
    queryKey: ["agency-overview"],
    queryFn: () => fetchOverview(),
    enabled: !!email,
    staleTime: 30_000,
  });

  const signOut = async () => {
    await supabase.auth.signOut();
    router.navigate({ to: "/login" });
  };

  if (!hydrated || !email) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  const balance = overview.data?.balance ?? 0;

  return (
    <div className="min-h-dvh bg-background">
      <header className="border-b border-border bg-card/30">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-6 py-4">
          <Link
            to="/agency"
            className="font-display text-lg font-semibold text-foreground"
          >
            Shugert<span style={{ color: GOLD }}>GEO</span> · Agency
          </Link>
          <nav className="flex items-center gap-1 text-xs font-medium uppercase tracking-[0.18em]">
            <NavLink to="/agency" exact>
              Overview
            </NavLink>
            <NavLink to="/agency/audits">Audits</NavLink>
            <NavLink to="/agency/brand">Brand</NavLink>
            <NavLink to="/agency/billing">Billing</NavLink>
          </nav>
          <div className="flex items-center gap-3">
            <span
              className="rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em]"
              style={{ background: GOLD, color: "#0a0a0a" }}
              title="Available audit credits"
            >
              {balance} credit{balance === 1 ? "" : "s"}
            </span>
            <button
              onClick={signOut}
              className="rounded-full border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-card"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-6 py-10">
        <Outlet />
      </main>
    </div>
  );
}

function NavLink({
  to,
  children,
  exact,
}: {
  to: string;
  children: React.ReactNode;
  exact?: boolean;
}) {
  return (
    <Link
      to={to}
      activeOptions={{ exact: !!exact }}
      className="rounded-full px-3 py-1.5 text-muted-foreground hover:text-foreground"
      activeProps={{
        className:
          "rounded-full px-3 py-1.5 text-foreground bg-card border border-border",
      }}
    >
      {children}
    </Link>
  );
}